import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAccount } from './AccountContext';
import logger from '../../../utils/logger';
import './SpecialDayEDMs.css';
import SchedulePostModal from '../../Dashboard/components/SchedulePostModal';
import GMBService from '../../../services/GMBService';
import ApiService from '../../../services/api';

const templateData = [
  // World Refugee Day
  ['home.jpg', 'hand.jpg', 'car1.jpg'],
  // Happy Father's Day
  ['car2.jpg', 'hand.jpg', 'home copy.jpg'],
  // Happy Mother's Day
  ['home.jpg', 'hand copy.jpg', 'crackers.jpg'],
  // World Wind Day
  ['GreenPeak Marketing.png', 'car4.jpeg', 'Plot  on sale.png'],
];
const productTemplateNames = [
  'World Refugee Day',
  "Happy Father's Day",
  "Happy Mother's Day",
  'World Wind Day',
];
const specialDayTemplateNames = productTemplateNames;

const shareOptions = [
  { icon: 'fas fa-envelope', label: 'Email' },
  { icon: 'fab fa-facebook', label: 'Facebook' },
  { icon: 'fab fa-whatsapp', label:'WhatsApp' },
  { icon: 'fab fa-instagram', label: 'Instagram' },
  { icon: 'fab fa-google', label: 'Google Profile' },
  { icon: 'fas fa-link', label: 'Copy link' },
];

const SpecialDayEDMs = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { uploadedLogo, isLogoUploaded, loadAccountInfo, accountInfo, selectedFontStyle } = useAccount();
  const selectedTemplate = location.state?.selectedTemplate || 'Birthday';
  const selectedSpecialDay = location.state?.selectedSpecialDay;

  // Load account info when component mounts
  useEffect(() => {
    loadAccountInfo();
  }, []);

  // Function to get font family based on selected font style
  const getFontFamily = (fontStyle) => {
    const fontMappings = {
      'Modern': 'Arial, Helvetica, sans-serif',
      'Elegant': 'Georgia, Times New Roman, serif',
      'Slab': 'Roboto Slab, Georgia, serif',
      'Hand Written': 'Cursive, Brush Script MT, cursive',
      'Playful': 'Comic Sans MS, cursive, sans-serif',
      'Futuristic': 'Orbitron, Arial, sans-serif'
    };
    return fontMappings[fontStyle] || 'Arial, sans-serif';
  };

  // Determine which template names to use and initial index
  let templateNames, initialTemplateIndex;
  if (selectedTemplate) {
    templateNames = productTemplateNames;
    initialTemplateIndex = productTemplateNames.findIndex(name => name.toLowerCase() === selectedTemplate.toLowerCase());
  } else if (selectedSpecialDay) {
    templateNames = specialDayTemplateNames;
    initialTemplateIndex = specialDayTemplateNames.findIndex(name => name.toLowerCase() === selectedSpecialDay.toLowerCase());
  } else {
    templateNames = productTemplateNames;
    initialTemplateIndex = 0;
  }

  const [currentTemplate, setCurrentTemplate] = useState(initialTemplateIndex >= 0 ? initialTemplateIndex : 0);
  const [imageOrder, setImageOrder] = useState(templateData.map(() => [0, 1, 2]));
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [selectedDesign, setSelectedDesign] = useState('premium'); // Add design state
  const [mainImg, setMainImg] = useState(templateData[initialTemplateIndex >= 0 ? initialTemplateIndex : 0][1]);
  const [logoPosition, setLogoPosition] = useState(50); // Logo position (0-100)
  const [isDraggingLogo, setIsDraggingLogo] = useState(false);
  const [customFooterColor, setCustomFooterColor] = useState(() => {
    try {
      const savedColor = localStorage.getItem('visibeen.customFooterColor');
      return savedColor || '#4A90E2';
    } catch (e) {
      return '#4A90E2';
    }
  });
  const [customFontColor, setCustomFontColor] = useState(() => {
    try {
      const savedColor = localStorage.getItem('visibeen.customFontColor');
      return savedColor || '#ffffff';
    } catch (e) {
      return '#ffffff';
    }
  });
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [schedulePostModalOpen, setSchedulePostModalOpen] = useState(false);
  const [capturedImageForSchedule, setCapturedImageForSchedule] = useState(null);
  const [gmbAccountId, setGmbAccountId] = useState(null);
  const [gmbLocationId, setGmbLocationId] = useState(null);
  const [showProfileSelector, setShowProfileSelector] = useState(false);
  const [availableProfiles, setAvailableProfiles] = useState([]);
  const [loadingProfiles, setLoadingProfiles] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const dropdownRef = useRef(null);
  const shareRef = useRef(null);
  const largePreviewRef = useRef(null);

  // Update mainImg when template changes
  useEffect(() => {
    setMainImg(templateData[currentTemplate][1]);
  }, [currentTemplate]);

  // Load GMB account and location IDs on component mount (use same keys as Dashboard)
  useEffect(() => {
    const loadGMBInfo = async () => {
      try {
        // Use the same localStorage keys as Dashboard and PostGenerationResults
        const savedAccount = localStorage.getItem('selectedGMBAccount');
        const savedLocation = localStorage.getItem('selectedGMBLocation');
        
        if (savedAccount && savedLocation) {
          const account = JSON.parse(savedAccount);
          const location = JSON.parse(savedLocation);
          
          const accountId = account.name?.split('/').pop();
          const locationId = location.name?.split('/').pop();
          
          if (accountId && locationId) {
            setGmbAccountId(accountId);
            setGmbLocationId(locationId);
            setSelectedProfile({
              accountId,
              locationId,
              businessName: location.title || account.accountName,
              address: location.storefrontAddress?.formattedAddress || '',
              isVerified: true
            });
            logger.log('[SpecialDayEDMs] ✅ Loaded GMB profile from Dashboard selection:', {
              accountId,
              locationId,
              businessName: location.title
            });
            return;
          }
        }
        
        logger.log('[SpecialDayEDMs] No saved GMB profile found - please select one from Dashboard');
      } catch (error) {
        logger.error('[SpecialDayEDMs] Error loading GMB info:', error);
      }
    };
    
    loadGMBInfo();
  }, []);

  // Function to fetch all available GMB profiles (via backend to avoid CORS)
  const fetchGMBProfiles = async () => {
    setLoadingProfiles(true);
    try {
      const accessToken = await GMBService.getAccessToken();
      if (!accessToken) {
        alert('Please sign in with Google first to access your business profiles.');
        return [];
      }

      logger.log('[SpecialDayEDMs] Fetching GMB accounts (backend)...');
      const accountsResp = await ApiService.get(`/customer/profile/getGmbAccount?googleAccessToken=${encodeURIComponent(accessToken)}`);
      const accountsRaw = accountsResp?.data || accountsResp;
      const accounts = Array.isArray(accountsRaw) ? accountsRaw : (accountsRaw?.accounts || []);

      if (!accounts || accounts.length === 0) {
        alert('No Google My Business accounts found. Please set up your GMB account first.');
        return [];
      }

      const profiles = [];

      for (const account of accounts) {
        try {
          const accountMatch = account.name?.match(/accounts\/(\d+)/);
          const accountId = accountMatch ? accountMatch[1] : account.name?.split('/').pop();

          logger.log('[SpecialDayEDMs] Fetching locations (backend) for account:', accountId);
          const locResp = await ApiService.get(`/customer/profile/getGmbProfile/${accountId}?googleAccessToken=${encodeURIComponent(accessToken)}`);
          const locationsRaw = locResp?.data || locResp;
          const locations = Array.isArray(locationsRaw) ? locationsRaw : (locationsRaw?.locations || []);

          for (const location of locations) {
            const locationMatch = location.name?.match(/locations\/(\d+)/);
            const locationId = locationMatch ? locationMatch[1] : location.name?.split('/').pop();

            profiles.push({
              accountId: accountId,
              locationId: locationId,
              businessName: location.title || location.locationName || 'Unnamed Business',
              address: formatAddress(location.storefrontAddress || location.address),
              category: location.categories?.primaryCategory?.displayName || location.primaryCategory?.displayName || 'Business',
              phoneNumber: location.phoneNumbers?.primaryPhone || location.primaryPhone || '',
              isVerified: !!location.metadata,
              fullLocationData: location
            });
          }
        } catch (err) {
          logger.error('[SpecialDayEDMs] Error fetching locations for account:', account?.name, err);
        }
      }

      logger.log('[SpecialDayEDMs] Profiles loaded:', profiles.length);
      setAvailableProfiles(profiles);
      return profiles;
    } catch (error) {
      logger.error('[SpecialDayEDMs] Error fetching GMB profiles:', error);
      alert('Failed to load GMB profiles. Please try again.');
      return [];
    } finally {
      setLoadingProfiles(false);
    }
  };

  // Helper function to format address
  const formatAddress = (address) => {
    if (!address) return 'No address';
    if (typeof address === 'string') return address;
    
    const parts = [];
    if (address.addressLines) parts.push(...address.addressLines);
    if (address.locality) parts.push(address.locality);
    if (address.administrativeArea) parts.push(address.administrativeArea);
    if (address.postalCode) parts.push(address.postalCode);
    
    return parts.join(', ') || 'No address';
  };

  // Handle profile selection
  const handleProfileSelect = (profile) => {
    setGmbAccountId(profile.accountId);
    setGmbLocationId(profile.locationId);
    setSelectedProfile(profile);
    
    // Note: We don't need to save to localStorage here - use Dashboard's "Set as Default" instead
    
    logger.log('[SpecialDayEDMs] Profile selected:', profile);
    setShowProfileSelector(false);
    
    // Now open the schedule modal
    setTimeout(() => {
      handleCaptureAndSchedule();
    }, 300);
  };

  // Logo interaction functions
  const handleLogoMouseDown = (e) => {
    e.preventDefault();
    setIsDraggingLogo(true);
  };

  const handleLogoMouseMove = (e) => {
    if (!isDraggingLogo) return;
    
    const container = e.currentTarget;
    const rect = container.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setLogoPosition(percentage);
  };

  const handleLogoMouseUp = () => {
    setIsDraggingLogo(false);
  };

  const moveLogoLeft = () => {
    setLogoPosition(prev => Math.max(0, prev - 10));
  };

  const moveLogoRight = () => {
    setLogoPosition(prev => Math.min(100, prev + 10));
  };

  // Color picker functions
  const handleColorChange = (color) => {
    setCustomFooterColor(color);
  };

  const toggleColorPicker = () => {
    setShowColorPicker(!showColorPicker);
  };

  // Design variations for account info overlay
  const designVariations = {
    premium: {
      name: 'Premium Gold',
      background: 'linear-gradient(135deg, rgba(0,0,0,0.95) 0%, rgba(20,20,20,0.85) 30%, rgba(40,40,40,0.75) 70%, rgba(60,60,60,0.6) 100%)',
      accentColor: '#FFD700',
      borderColor: 'rgba(255,215,0,0.8)',
      textGradient: 'linear-gradient(45deg, #FFD700 0%, #FFA500 50%, #FFD700 100%)'
    },
    modern: {
      name: 'Modern Blue',
      background: 'linear-gradient(135deg, rgba(0,0,0,0.9) 0%, rgba(30,30,60,0.8) 50%, rgba(60,60,100,0.7) 100%)',
      accentColor: '#4FC3F7',
      borderColor: 'rgba(79,195,247,0.8)',
      textGradient: 'linear-gradient(45deg, #4FC3F7 0%, #29B6F6 50%, #4FC3F7 100%)'
    },
    elegant: {
      name: 'Elegant Purple',
      background: 'linear-gradient(135deg, rgba(0,0,0,0.92) 0%, rgba(50,20,80,0.85) 40%, rgba(80,40,120,0.75) 100%)',
      accentColor: '#E1BEE7',
      borderColor: 'rgba(225,190,231,0.8)',
      textGradient: 'linear-gradient(45deg, #E1BEE7 0%, #CE93D8 50%, #E1BEE7 100%)'
    },
    vibrant: {
      name: 'Vibrant Orange',
      background: 'linear-gradient(135deg, rgba(0,0,0,0.9) 0%, rgba(80,40,0,0.8) 50%, rgba(120,60,0,0.7) 100%)',
      accentColor: '#FF9800',
      borderColor: 'rgba(255,152,0,0.8)',
      textGradient: 'linear-gradient(45deg, #FF9800 0%, #FF5722 50%, #FF9800 100%)'
    },
    minimal: {
      name: 'Minimal White',
      background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(240,240,240,0.9) 100%)',
      accentColor: '#333333',
      borderColor: 'rgba(51,51,51,0.8)',
      textGradient: 'linear-gradient(45deg, #333333 0%, #666666 50%, #333333 100%)'
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
      if (shareRef.current && !shareRef.current.contains(event.target)) {
        setShareOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const updateTemplate = (idx) => {
    setCurrentTemplate(idx);
    setDropdownOpen(false);
  };
  const rotateLeft = () => {
    setImageOrder((prev) => {
      const newOrder = [...prev];
      const order = newOrder[currentTemplate];
      newOrder[currentTemplate] = [order[1], order[2], order[0]];
      return newOrder;
    });
  };
  const rotateRight = () => {
    setImageOrder((prev) => {
      const newOrder = [...prev];
      const order = newOrder[currentTemplate];
      newOrder[currentTemplate] = [order[2], order[0], order[1]];
      return newOrder;
    });
  };

  const handleOpenSchedulePostModal = async () => {
    logger.log('[SpecialDayEDMs] Opening schedule post modal...');
    
    // Check if GMB profile is selected
    if (!gmbAccountId || !gmbLocationId) {
      logger.log('[SpecialDayEDMs] No GMB profile selected');
      alert('Please select a default GMB profile first.\n\nGo to Dashboard → Click "Set as Default" on your business profile.');
      setShareOpen(false);
      return;
    }
    
    // Profile is selected, proceed with capture and schedule
    handleCaptureAndSchedule();
  };

  const handleCaptureAndSchedule = async () => {
    // Capture the post as image first
    const postElement = largePreviewRef.current;
    if (!postElement) {
      logger.error('[SpecialDayEDMs] Post preview element not found');
      return;
    }
    
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(postElement, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        logging: false
      });
      
      // Convert canvas to blob
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png', 1.0));
      const file = new File([blob], `specialday-edm-${Date.now()}.png`, { type: 'image/png' });
      
      // Open schedule post modal directly with the captured image
      setCapturedImageForSchedule(file);
      setSchedulePostModalOpen(true);
      setShareOpen(false); // Close share menu
      
      logger.log('[SpecialDayEDMs] Schedule post modal opened with template image');
    } catch (err) {
      logger.error('[SpecialDayEDMs] Error capturing post:', err);
      alert('Failed to capture image. Please try again.');
    }
  };

  const handleCloseSchedulePostModal = () => {
    setSchedulePostModalOpen(false);
    setCapturedImageForSchedule(null);
  };

  const handlePostScheduled = () => {
    logger.log('[SpecialDayEDMs] Post scheduled successfully');
    setSchedulePostModalOpen(false);
    setCapturedImageForSchedule(null);
    alert('Post scheduled successfully to Google My Business!');
  };

  const downloadImage = async () => {
    try {
      // Get the large preview element
      const previewElement = document.querySelector('.large-preview');
      if (!previewElement) {
        logger.error('Preview element not found');
        return;
      }

      // Create a canvas element
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Get the image element inside the preview
      const imgElement = previewElement.querySelector('img');
      if (!imgElement) {
        logger.error('Image element not found');
        return;
      }

      // Set canvas size
      canvas.width = imgElement.naturalWidth || 800;
      canvas.height = imgElement.naturalHeight || 600;

      // Create a new image to avoid CORS issues
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        // Draw the image
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        
        // Get the footer element
        const footerElement = previewElement.querySelector('[style*="position: absolute"]');
        if (footerElement && accountInfo) {
          // Calculate footer position and size
          const footerRect = footerElement.getBoundingClientRect();
          const previewRect = previewElement.getBoundingClientRect();
          
          // Calculate relative position
          const footerX = (footerRect.left - previewRect.left) * (canvas.width / previewRect.width);
          const footerY = (footerRect.top - previewRect.top) * (canvas.height / previewRect.height);
          const footerWidth = footerRect.width * (canvas.width / previewRect.width);
          const footerHeight = footerRect.height * (canvas.height / previewRect.height);
          
          // Get computed styles
          const footerStyles = window.getComputedStyle(footerElement);
          
          // Draw footer background - purple gradient like reference
          const gradient = ctx.createLinearGradient(footerX, footerY, footerX, footerY + footerHeight);
          gradient.addColorStop(0, `${customFooterColor}dd`); // Dark version
          gradient.addColorStop(1, `${customFooterColor}aa`); // Light version
          
          ctx.fillStyle = gradient;
          ctx.fillRect(footerX, footerY, footerWidth, footerHeight);
          
          // Draw text with exact positioning like reference
          if (accountInfo) {
            const selectedFont = getFontFamily(selectedFontStyle);
            
            // Company name - centered at top of footer
            if (accountInfo.businessName) {
              ctx.font = `bold 20px ${selectedFont}`;
              ctx.fillStyle = customFontColor;
              ctx.textAlign = 'center';
              ctx.fillText(accountInfo.businessName.toUpperCase(), footerWidth/2, footerY + 25);
            }
            
            // Address - left aligned with better spacing
            if (accountInfo.address) {
              ctx.font = `14px ${selectedFont}`;
              ctx.fillStyle = customFontColor;
              ctx.textAlign = 'left';
              ctx.fillText(`Address: ${accountInfo.address}`, 15, footerY + 50);
            }
            
            // Contact - left aligned with better spacing
            if (accountInfo.contact) {
              ctx.font = `14px ${selectedFont}`;
              ctx.fillStyle = customFontColor;
              ctx.textAlign = 'left';
              ctx.fillText(`Contact: ${accountInfo.contact}`, 15, footerY + 70);
            }
            
            // Website - right aligned with better spacing
            if (accountInfo.website) {
              ctx.font = `14px ${selectedFont}`;
              ctx.fillStyle = customFontColor;
              ctx.textAlign = 'right';
              ctx.fillText(`Website: ${accountInfo.website}`, footerWidth - 15, footerY + 70);
            }
          }
        }

        // Draw uploaded logo if present
        if (uploadedLogo && isLogoUploaded) {
          const logoImg = new Image();
          logoImg.crossOrigin = 'anonymous';
          logoImg.onload = () => {
            const logoSize = 70;
            const logoX = 20; // Fixed position in top-left
            const logoY = 20;
            
            // Draw logo background
            ctx.fillStyle = 'rgba(255,255,255,0.95)';
            ctx.fillRect(logoX - 3, logoY - 3, logoSize + 6, logoSize + 6);
            
            // Draw logo
            ctx.drawImage(logoImg, logoX, logoY, logoSize, logoSize);
            
            // Convert canvas to blob and download
            canvas.toBlob((blob) => {
              const url = URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = `visibeen-edm-${Date.now()}.png`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(url);
            }, 'image/png');
          };
          logoImg.src = uploadedLogo;
        } else {
          // Convert canvas to blob and download
          canvas.toBlob((blob) => {
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `visibeen-edm-${Date.now()}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
          }, 'image/png');
        }
      };

      img.onerror = () => {
        // Fallback: download the original image
    const link = document.createElement('a');
        link.href = imgElement.src;
        link.download = `visibeen-edm-${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

      // Set the image source
      img.src = imgElement.src;
      
    } catch (error) {
      logger.error('Error downloading image:', error);
      // Fallback: try to download the original image
      const imgElement = document.querySelector('.large-preview img');
      if (imgElement) {
        const link = document.createElement('a');
        link.href = imgElement.src;
        link.download = `visibeen-edm-${Date.now()}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }
  };

  const leftImg = templateData[currentTemplate][imageOrder[currentTemplate][0]];
  const rightImg = templateData[currentTemplate][imageOrder[currentTemplate][2]];

  // Component to display image with account info overlay
  const ImageWithAccountInfo = ({ imageSrc, isUploadedLogo = false, imageIndex = 0, isMainPreview = false }) => {
    // Create attractive footer design based on image colors
    const createAttractiveImageBasedDesign = (imageIndex) => {
      const selectedFont = getFontFamily(selectedFontStyle);
      
      // Different attractive footer styles that complement images
      const attractiveFooterStyles = {
        0: { // First photo - Vibrant & Modern
          background: `linear-gradient(135deg, ${customFooterColor}dd, ${customFooterColor}aa)`,
          companyFont: selectedFont,
          companyColor: customFontColor,
          detailFont: selectedFont,
          detailColor: customFontColor,
          borderStyle: '2px solid rgba(255,255,255,0.4)',
          companySize: '12px',
          detailSize: '9px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
          backdropFilter: 'blur(10px)'
        },
        1: { // Second photo - Elegant & Professional
          background: `linear-gradient(135deg, ${customFooterColor}dd, ${customFooterColor}aa)`,
          companyFont: selectedFont,
          companyColor: customFontColor,
          detailFont: selectedFont,
          detailColor: customFontColor,
          borderStyle: '2px solid rgba(255,255,255,0.3)',
          companySize: '11px',
          detailSize: '8px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
          backdropFilter: 'blur(10px)'
        },
        2: { // Third photo - Sophisticated & Clean
          background: `linear-gradient(135deg, ${customFooterColor}dd, ${customFooterColor}aa)`,
          companyFont: selectedFont,
          companyColor: customFontColor,
          detailFont: selectedFont,
          detailColor: customFontColor,
          borderStyle: 'none',
          companySize: '10px',
          detailSize: '7px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
          backdropFilter: 'blur(10px)'
        }
      };

      return attractiveFooterStyles[imageIndex] || attractiveFooterStyles[0];
    };

    const currentDesign = createAttractiveImageBasedDesign(imageIndex);
    
    return (
      <div style={{ 
        position: 'relative', 
        display: 'inline-block',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        borderRadius: '12px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease'
      }}
      onMouseMove={isMainPreview ? handleLogoMouseMove : undefined}
      onMouseUp={isMainPreview ? handleLogoMouseUp : undefined}
      onMouseLeave={isMainPreview ? handleLogoMouseUp : undefined}
      >
        <img 
          src={imageSrc} 
          alt="Template Image" 
          style={{ 
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transition: 'transform 0.3s ease'
          }} 
        />
        
        {/* Draggable Logo Overlay */}
        {uploadedLogo && isLogoUploaded && isMainPreview && (
          <div
            style={{
              position: 'absolute',
              top: '20px',
              left: `${logoPosition}%`,
              transform: 'translateX(-50%)',
              cursor: isDraggingLogo ? 'grabbing' : 'grab',
              zIndex: 10,
              transition: isDraggingLogo ? 'none' : 'left 0.1s ease',
              userSelect: 'none'
            }}
            onMouseDown={handleLogoMouseDown}
          >
            <img
              src={uploadedLogo}
              alt="Uploaded Logo"
              style={{
                width: '80px',
                height: '80px',
                objectFit: 'contain',
                filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.5))',
                borderRadius: '8px',
                backgroundColor: 'rgba(255,255,255,0.9)',
                padding: '5px',
                border: '2px solid rgba(255,255,255,0.8)'
              }}
            />
          </div>
        )}
        
        {/* Dynamic Footer Design */}
        <div style={{
          position: 'absolute',
          bottom: '0',
          left: '0',
          right: '0',
          background: currentDesign.background,
          color: currentDesign.companyColor,
          padding: '6px 12px',
          fontSize: '10px',
          lineHeight: '1.2',
          borderBottomLeftRadius: '12px',
          borderBottomRightRadius: '12px',
          backdropFilter: currentDesign.backdropFilter,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          minHeight: '50px',
          border: currentDesign.borderStyle,
          boxShadow: currentDesign.boxShadow,
          transition: 'all 0.3s ease'
        }}>
          {/* Top content */}
          <div style={{ flex: '1' }}>
            {/* Company Name - Dynamic styling */}
            {accountInfo.businessName && (
              <div style={{ 
                fontWeight: 'bold',
                fontSize: currentDesign.companySize,
                marginBottom: '4px',
                color: currentDesign.companyColor,
                letterSpacing: '0.5px',
                fontFamily: currentDesign.companyFont,
                textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
                textTransform: 'uppercase'
              }}>
                {accountInfo.businessName}
              </div>
            )}
            
            {/* Address - Dynamic styling */}
            {accountInfo.address && (
              <div style={{ 
                fontSize: currentDesign.detailSize,
                marginBottom: '3px',
                marginTop: '6px',
                color: currentDesign.detailColor,
                textAlign: 'left',
                fontFamily: currentDesign.detailFont,
                textShadow: '0.5px 0.5px 1px rgba(0,0,0,0.3)',
                lineHeight: '1.2'
              }}>
                Address: {accountInfo.address}
              </div>
            )}
          </div>
          
          {/* Bottom row - Contact and Website */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'flex-end',
            marginTop: '8px'
          }}>
            {/* Contact Numbers - Bottom left */}
            {accountInfo.contact && (
              <div style={{ 
                fontSize: currentDesign.detailSize,
                color: currentDesign.detailColor,
                textAlign: 'left',
                fontFamily: currentDesign.detailFont,
                fontWeight: '500',
                textShadow: '0.5px 0.5px 1px rgba(0,0,0,0.3)',
                lineHeight: '1.2'
              }}>
                Contact: {accountInfo.contact}
                {accountInfo.altContact && `, ${accountInfo.altContact}`}
              </div>
            )}
            
            {/* Website - Bottom right */}
            {accountInfo.website && (
              <div style={{ 
                fontSize: currentDesign.detailSize,
                color: currentDesign.detailColor,
                textAlign: 'right',
                fontFamily: currentDesign.detailFont,
                fontWeight: '400',
                textShadow: '0.5px 0.5px 1px rgba(0,0,0,0.3)',
                lineHeight: '1.2'
              }}>
                Website: {accountInfo.website}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Header */}
      <div className="header">
        <div className="header-container">
          <div className="header-logo-block">
            <img src="VISIBEN.svg" alt="Logo" className="logo-image" />
          </div>
          <div className="header-nav">
            <a href="#">Change design</a>
            <a href="#">Upload logo</a>
            <a href="#">My profile</a>
            <a href="#">Logout</a>
          </div>
        </div>
      </div>
      {/* Main Content */}
      <div className="main-content">
        <div className="main-title">
          <div className="products-title">{selectedTemplate ? 'Products EDMs' : selectedSpecialDay ? 'Special Days EDMs' : 'Products EDMs'}</div>
          <div className="products-subtitle">Select your Template</div>
        </div>
        {/* Dropdown */}
        <div className="template-section">
          <div className={`custom-dropdown${dropdownOpen ? ' open' : ''}`} ref={dropdownRef}>
            <div className="custom-dropdown-selected" onClick={() => setDropdownOpen((open) => !open)}>
              {templateNames[currentTemplate]} <span className="dropdown-arrow">&#9660;</span>
            </div>
            <ul className="custom-dropdown-list">
              {templateNames.map((name, idx) => (
                <li key={name} onClick={() => updateTemplate(idx)}>{name}</li>
              ))}
            </ul>
          </div>
        </div>
        
        {/* Card Previews */}
        <div className="card-preview-row">
          {templateData[currentTemplate].map((img, idx) => (
            <div key={`${img}-${idx}`} className={`card-preview${mainImg === img ? ' selected' : ''}`} onClick={() => {
              // Set the clicked image as the main preview
              setMainImg(img);
            }}>
              <ImageWithAccountInfo imageSrc={require(`./${img}`)} imageIndex={idx} isMainPreview={false} />
            </div>
          ))}
        </div>
        {/* Large Preview */}
        <div className="large-preview" ref={largePreviewRef}>
          <ImageWithAccountInfo imageSrc={require(`./${mainImg}`)} imageIndex={templateData[currentTemplate].indexOf(mainImg)} isMainPreview={true} />
        </div>
        {/* Selected GMB Profile Indicator */}
        {selectedProfile && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '15px',
            marginTop: '20px',
            padding: '15px 20px',
            backgroundColor: 'rgba(66, 133, 244, 0.1)',
            borderRadius: '12px',
            border: '2px solid rgba(66, 133, 244, 0.3)'
          }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                <i className="fab fa-google" style={{ marginRight: '6px', color: '#4285F4' }}></i>
                Selected GMB Profile:
              </div>
              <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#333' }}>
                {selectedProfile.businessName}
              </div>
            </div>
            <button
              onClick={() => setShowProfileSelector(true)}
              style={{
                padding: '8px 16px',
                backgroundColor: '#4285F4',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '600',
                transition: 'background-color 0.3s ease',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#3367D6'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#4285F4'}
            >
              Change Profile
            </button>
          </div>
        )}

        {/* Action Buttons */}
        <div className="action-buttons">
          <button className="btn-download" onClick={downloadImage}>Download</button>
          <div className="share-btn-wrapper" ref={shareRef}>
            <button className="btn-share" onClick={() => setShareOpen((open) => !open)} id="shareBtn">Share</button>
            {shareOpen && (
              <div className="share-menu" id="shareMenu">
                <ul>
                  {shareOptions.map((opt, idx) => (
                    <li 
                      key={opt.label}
                      onClick={() => {
                        if (opt.label === 'Google Profile') {
                          handleOpenSchedulePostModal();
                        }
                      }}
                      style={{ cursor: opt.label === 'Google Profile' ? 'pointer' : 'default' }}
                    >
                      <i className={opt.icon}></i> {opt.label} {opt.extra || null}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
        
        {/* Logo Control Buttons */}
        {uploadedLogo && isLogoUploaded && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '20px',
            marginTop: '20px',
            padding: '15px',
            backgroundColor: 'rgba(255,255,255,0.1)',
            borderRadius: '10px',
            backdropFilter: 'blur(10px)'
          }}>
            <button 
              onClick={moveLogoLeft}
              style={{
                padding: '10px 20px',
                backgroundColor: '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold',
                transition: 'background-color 0.3s ease'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#45a049'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#4CAF50'}
            >
              ← Move Logo Left
            </button>
            <div style={{
              padding: '10px 20px',
              backgroundColor: 'rgba(0,0,0,0.7)',
              color: 'white',
              borderRadius: '5px',
              fontSize: '14px',
              fontWeight: 'bold'
            }}>
              Position: {Math.round(logoPosition)}%
            </div>
            <button 
              onClick={moveLogoRight}
              style={{
                padding: '10px 20px',
                backgroundColor: '#2196F3',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold',
                transition: 'background-color 0.3s ease'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#1976D2'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#2196F3'}
            >
              Move Logo Right →
            </button>
          </div>
        )}
        
        {/* Color Pickers Container */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '15px',
          marginTop: '20px',
          padding: '20px',
          backgroundColor: 'rgba(255,255,255,0.95)',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
        }}>
          {/* Footer Color Picker */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '15px',
            flexWrap: 'wrap'
          }}>
            <div style={{ fontSize: '14px', fontWeight: '600', color: '#333', minWidth: '140px' }}>
              Footer Background:
            </div>
            <div style={{ position: 'relative' }}>
              <input
                type="color"
                value={customFooterColor}
                onChange={(e) => setCustomFooterColor(e.target.value)}
                style={{
                  width: '50px',
                  height: '35px',
                  border: '2px solid #ddd',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  outline: 'none'
                }}
              />
            </div>
            <div style={{
              padding: '6px 12px',
              backgroundColor: customFooterColor,
              color: customFontColor,
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: '500',
              minWidth: '90px',
              textAlign: 'center',
              boxShadow: '0 2px 6px rgba(0,0,0,0.2)'
            }}>
              {customFooterColor}
            </div>
            <button
              onClick={() => setCustomFooterColor('#4A90E2')}
              style={{
                padding: '6px 14px',
                backgroundColor: '#f0f0f0',
                border: '1px solid #ddd',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '500',
                transition: 'all 0.3s ease'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#e0e0e0'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#f0f0f0'}
            >
              Reset
            </button>
          </div>

          {/* Font Color Picker */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '15px',
            flexWrap: 'wrap',
            paddingTop: '10px',
            borderTop: '1px solid #eee'
          }}>
            <div style={{ fontSize: '14px', fontWeight: '600', color: '#333', minWidth: '140px' }}>
              Font Color:
            </div>
            <div style={{ position: 'relative' }}>
              <input
                type="color"
                value={customFontColor}
                onChange={(e) => setCustomFontColor(e.target.value)}
                style={{
                  width: '50px',
                  height: '35px',
                  border: '2px solid #ddd',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  outline: 'none'
                }}
              />
            </div>
            <div style={{
              padding: '6px 12px',
              backgroundColor: customFooterColor,
              color: customFontColor,
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: '500',
              minWidth: '90px',
              textAlign: 'center',
              boxShadow: '0 2px 6px rgba(0,0,0,0.2)'
            }}>
              {customFontColor}
            </div>
            <button
              onClick={() => setCustomFontColor('#ffffff')}
              style={{
                padding: '6px 14px',
                backgroundColor: '#f0f0f0',
                border: '1px solid #ddd',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '500',
                transition: 'all 0.3s ease'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#e0e0e0'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#f0f0f0'}
            >
              Reset
            </button>
          </div>

          {/* Quick Color Presets */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '10px',
            paddingTop: '10px',
            borderTop: '1px solid #eee',
            flexWrap: 'wrap'
          }}>
            <span style={{ fontSize: '13px', fontWeight: '500', color: '#666' }}>Quick Presets:</span>
            {[
              { bg: '#000000', font: '#ffffff', label: 'Dark' },
              { bg: '#ffffff', font: '#000000', label: 'Light' },
              { bg: '#1e3a5f', font: '#ffffff', label: 'Navy' },
              { bg: '#8b0000', font: '#ffd700', label: 'Royal' },
              { bg: '#2d5a27', font: '#ffffff', label: 'Forest' }
            ].map((preset, idx) => (
              <button
                key={idx}
                onClick={() => {
                  setCustomFooterColor(preset.bg);
                  setCustomFontColor(preset.font);
                }}
                style={{
                  padding: '6px 12px',
                  backgroundColor: preset.bg,
                  color: preset.font,
                  border: '2px solid #ddd',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '600',
                  transition: 'all 0.3s ease'
                }}
              >
                {preset.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Color Picker Modal */}
        {showColorPicker && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }} onClick={toggleColorPicker}>
            <div style={{
              backgroundColor: 'white',
              padding: '30px',
              borderRadius: '10px',
              boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
              minWidth: '300px'
            }} onClick={(e) => e.stopPropagation()}>
              <h3 style={{ margin: '0 0 20px 0', textAlign: 'center' }}>Choose Footer Color</h3>
              
              {/* Preset Colors */}
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ margin: '0 0 10px 0' }}>Preset Colors:</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '10px' }}>
                  {['#4A90E2', '#E74C3C', '#2ECC71', '#F39C12', '#9B59B6', '#1ABC9C', '#E67E22', '#34495E', '#FF6B6B', '#4ECDC4'].map((color) => (
                    <button
                      key={color}
                      onClick={() => handleColorChange(color)}
                      style={{
                        width: '40px',
                        height: '40px',
                        backgroundColor: color,
                        border: '2px solid #ddd',
                        borderRadius: '5px',
                        cursor: 'pointer',
                        transition: 'transform 0.2s ease'
                      }}
                      onMouseOver={(e) => e.target.style.transform = 'scale(1.1)'}
                      onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
                      title={color}
                    />
                  ))}
                </div>
              </div>
              
              {/* Custom Color Picker */}
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ margin: '0 0 10px 0' }}>Custom Color:</h4>
                <input
                  type="color"
                  value={customFooterColor}
                  onChange={(e) => handleColorChange(e.target.value)}
                  style={{
                    width: '100%',
                    height: '50px',
                    border: '2px solid #ddd',
                    borderRadius: '5px',
                    cursor: 'pointer'
                  }}
                />
              </div>
              
              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                <button
                  onClick={toggleColorPicker}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#6C757D',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    handleColorChange('#4A90E2');
                    toggleColorPicker();
                  }}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#DC3545',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}
                >
                  Reset to Default
                </button>
              </div>
            </div>
          </div>
        )}

      {/* GMB Profile Selector Modal */}
      {showProfileSelector && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.7)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 2000,
          padding: '20px'
        }} onClick={() => setShowProfileSelector(false)}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '30px',
            maxWidth: '800px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }} onClick={(e) => e.stopPropagation()}>
            
            {/* Header */}
            <div style={{
              marginBottom: '25px',
              borderBottom: '2px solid #f0f0f0',
              paddingBottom: '15px'
            }}>
              <h2 style={{
                margin: '0 0 10px 0',
                fontSize: '24px',
                fontWeight: 'bold',
                color: '#333'
              }}>
                <i className="fab fa-google" style={{ color: '#4285F4', marginRight: '10px' }}></i>
                Select Your Business Profile
              </h2>
              <p style={{
                margin: 0,
                fontSize: '14px',
                color: '#666'
              }}>
                Choose which Google My Business profile to post this EDM to
              </p>
            </div>

            {/* Loading State */}
            {loadingProfiles && (
              <div style={{
                textAlign: 'center',
                padding: '40px 20px',
                color: '#666'
              }}>
                <div style={{
                  fontSize: '40px',
                  marginBottom: '15px'
                }}>⏳</div>
                <div style={{ fontSize: '16px' }}>Loading your business profiles...</div>
              </div>
            )}

            {/* Profile Cards */}
            {!loadingProfiles && availableProfiles.length > 0 && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                gap: '20px'
              }}>
                {availableProfiles.map((profile, index) => (
                  <div
                    key={`${profile.accountId}-${profile.locationId}`}
                    onClick={() => handleProfileSelect(profile)}
                    style={{
                      backgroundColor: '#fff',
                      border: '2px solid #e0e0e0',
                      borderRadius: '12px',
                      padding: '20px',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      position: 'relative'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#4285F4';
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 8px 20px rgba(66,133,244,0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#e0e0e0';
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                    }}
                  >
                    {/* Verified Badge */}
                    {profile.isVerified && (
                      <div style={{
                        position: 'absolute',
                        top: '15px',
                        right: '15px',
                        backgroundColor: '#4CAF50',
                        color: 'white',
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        <i className="fas fa-check-circle"></i>
                        Verified
                      </div>
                    )}

                    {/* Business Name */}
                    <div style={{
                      fontSize: '18px',
                      fontWeight: 'bold',
                      color: '#333',
                      marginBottom: '12px',
                      paddingRight: profile.isVerified ? '80px' : '0'
                    }}>
                      {profile.businessName}
                    </div>

                    {/* Category */}
                    <div style={{
                      fontSize: '13px',
                      color: '#4285F4',
                      fontWeight: '600',
                      marginBottom: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      <i className="fas fa-tag"></i>
                      {profile.category}
                    </div>

                    {/* Address */}
                    <div style={{
                      fontSize: '13px',
                      color: '#666',
                      marginBottom: '8px',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '6px',
                      lineHeight: '1.4'
                    }}>
                      <i className="fas fa-map-marker-alt" style={{ marginTop: '2px', flexShrink: 0 }}></i>
                      <span>{profile.address}</span>
                    </div>

                    {/* Phone Number */}
                    {profile.phoneNumber && (
                      <div style={{
                        fontSize: '13px',
                        color: '#666',
                        marginBottom: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <i className="fas fa-phone"></i>
                        {profile.phoneNumber}
                      </div>
                    )}

                    {/* IDs (for debugging) */}
                    <div style={{
                      marginTop: '15px',
                      paddingTop: '12px',
                      borderTop: '1px solid #f0f0f0',
                      fontSize: '11px',
                      color: '#999'
                    }}>
                      <div>Location ID: {profile.locationId}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* No Profiles Found */}
            {!loadingProfiles && availableProfiles.length === 0 && (
              <div style={{
                textAlign: 'center',
                padding: '40px 20px',
                color: '#666'
              }}>
                <div style={{
                  fontSize: '50px',
                  marginBottom: '15px'
                }}>🏢</div>
                <div style={{ 
                  fontSize: '18px',
                  fontWeight: 'bold',
                  marginBottom: '10px'
                }}>
                  No Business Profiles Found
                </div>
                <div style={{ fontSize: '14px' }}>
                  Please set up your Google My Business account from the Business Profile page.
                </div>
              </div>
            )}

            {/* Close Button */}
            <div style={{
              marginTop: '25px',
              paddingTop: '20px',
              borderTop: '2px solid #f0f0f0',
              display: 'flex',
              justifyContent: 'center'
            }}>
              <button
                onClick={() => setShowProfileSelector(false)}
                style={{
                  padding: '12px 30px',
                  backgroundColor: '#f0f0f0',
                  color: '#333',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '15px',
                  fontWeight: '600',
                  transition: 'background-color 0.3s ease'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#e0e0e0'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#f0f0f0'}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Post Modal for Google My Business */}
      <SchedulePostModal
        open={schedulePostModalOpen}
        onClose={handleCloseSchedulePostModal}
        onSuccess={handlePostScheduled}
        preloadedImage={capturedImageForSchedule}
        initialContent={selectedSpecialDay
          ? `${selectedSpecialDay}! 🎉\n\nCelebrating this special day with gratitude and joy.\n\n${accountInfo?.businessName ? `- ${accountInfo.businessName}` : ''}`
          : `Check out our ${selectedTemplate}! 🌟\n\nQuality products at great prices.\n\n${accountInfo?.businessName ? `- ${accountInfo.businessName}` : ''}`
        }
        businessName={accountInfo?.businessName || ''}
        businessAddress={accountInfo?.address || ''}
        businessKeywords={accountInfo?.keywords || []}
        accountId={gmbAccountId}
        locationId={gmbLocationId}
        businessCategory={accountInfo?.category || ''}
        businessCoordinates={accountInfo?.latlng || null}
        postName={selectedSpecialDay || selectedTemplate || 'Special Day Post'}
      />
      </div>
      {/* Footer */}
      <div className="footer">
        <div className="footer-container">
          <div className="footer-brand">
            <div className="footer-logo-block">
              <img src="VISIBEN.svg" alt="Logo" className="logo-image" />
            </div>
            <div className="footer-copyright">
              Copyright © 2025 e2e digitech pvt ltd.<br />All rights reserved
            </div>
            <div className="footer-social">
              <a href="#"><i className="fab fa-instagram"></i></a>
              <a href="#"><i className="fab fa-facebook"></i></a>
              <a href="#"><i className="fab fa-twitter"></i></a>
              <a href="#"><i className="fab fa-youtube"></i></a>
            </div>
          </div>
          <div className="footer-links">
            <div className="footer-section">
              <div className="footer-title">Company</div>
              <a href="#">About us</a>
              <a href="#">Blog</a>
              <a href="#">Contact us</a>
              <a href="#">Pricing</a>
              <a href="#">Testimonials</a>
            </div>
            <div className="footer-section">
              <div className="footer-title">Support</div>
              <a href="#">Help center</a>
              <a href="#">Terms of service</a>
              <a href="#">Legal</a>
              <a href="#">Privacy policy</a>
              <a href="#">Status</a>
            </div>
          </div>
          <div className="footer-col">
            <div className="footer-title">Stay up to date</div>
            <form className="footer-newsletter">
              <div className="input-icon-wrapper">
                <input type="email" placeholder="Your email address" />
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default SpecialDayEDMs; 
